import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, ErrorSummary, Button, Checkboxes, Input, Select as SimpleSelect, InsetText, ActionLink, Label } from "nhsuk-react-components";
import SelectOther from "../../../components/selectOther/SelectOther.component";
import ImplantInstanceList from "../../../components/implantInstanceList/ImplantInstanceList.component";
import EntitySelect from '../../../components/entitySelect/EntitySelect.component';
import ImplantHistoryDrawer from "../../../components/implantHistoryDrawer/ImplantHistoryDrawer.component";
import {
    listImplantCentres,
    listImplantCentresByIds
} from '../../../actions/implantCentreActions';
import {
    listFollowupCentres,
    listFollowupCentresByIds
} from '../../../actions/followupCentreActions';
import {
    listOrganisations,
    listOrganisationsByIds
} from '../../../actions/organisationActions';

const ImplantDetails = ({
    updatedPatient,
    onPatientImplantDetailsChange,
    error,
}) => {
    const dispatch = useDispatch();
    const [openDrawer, setOpenDrawer] = useState(false);



    const handleDeviceListConditionChange = useCallback((deviceList) => {
        onPatientImplantDetailsChange({
            ...updatedPatient,
            mrConditional: deviceList.isMRIConditional,
            deviceList
        });
    }, [updatedPatient, onPatientImplantDetailsChange]);


    const handleCheckboxChange = useCallback((field, value) => {
        onPatientImplantDetailsChange({
            ...updatedPatient,
            [field]: value
        });
    }, [updatedPatient, onPatientImplantDetailsChange]);

    return (
        <Card>
            <Card.Content>
                <Card.Heading className="nhsuk-heading-m">
                    Implant Details
                </Card.Heading>
                {error && (
                    <ErrorSummary>
                        <ErrorSummary.Title id="error-summary-title">
                            There is a problem
                        </ErrorSummary.Title>
                        <ErrorSummary.Body>
                            <ul className="nhsuk-list nhsuk-list--bullet">
                                {Array.isArray(error) ? error.map((err, index) => (
                                    <li key={index}>{err}</li>
                                )) : <li>{error}</li>}
                                {updatedPatient?.mrConditional === false && (
                                    <li style={{ color: 'red' }}>System is not MRI conditional</li>
                                )}
                                {updatedPatient?.mrConditional == null && (
                                    <li>System MRI conditional: Unknown</li>
                                )}
                            </ul>
                        </ErrorSummary.Body>
                    </ErrorSummary>
                )}

                <SelectOther
                    id="interventionCategory"
                    label="Intervention Category"
                    name="interventionCategory"
                    value={updatedPatient?.interventionCategory}
                    onChange={(e) => onPatientImplantDetailsChange({ ...updatedPatient, interventionCategory: e.target.value })}
                >
                    <SimpleSelect.Option value="">Select an intervention category</SimpleSelect.Option>
                    <SimpleSelect.Option value="No intervention (FU only of pt implanted elsewhere)">No intervention (FU only of pt implanted elsewhere)</SimpleSelect.Option>
                    <SimpleSelect.Option value="First ever pacemaker/ICD system">First ever pacemaker/ICD system</SimpleSelect.Option>
                    <SimpleSelect.Option value="Generator change only">Generator change only</SimpleSelect.Option>
                    <SimpleSelect.Option value="New generator and lead(s) revision/replacement">New generator and lead(s) revision/replacement</SimpleSelect.Option>
                    <SimpleSelect.Option value="Upgrade - increased functionality (to DDD, CRT, or ICD)">Upgrade - increased functionality (to DDD, CRT, or ICD)</SimpleSelect.Option>
                    <SimpleSelect.Option value="Downgrade - decreased functionality">Downgrade - decreased functionality</SimpleSelect.Option>
                    <SimpleSelect.Option value="System explant">System explant</SimpleSelect.Option>
                    <SimpleSelect.Option value="Wound revision/generator resite/haematoma">Wound revision/generator resite/haematoma</SimpleSelect.Option>
                    <SimpleSelect.Option value="Lead intervention only">Lead intervention only</SimpleSelect.Option>
                    <SimpleSelect.Option value="Monitor procedure only">Monitor procedure only</SimpleSelect.Option>
                    <SimpleSelect.Option value="Heart valve procedure">Heart valve procedure</SimpleSelect.Option>
                    <SimpleSelect.Option value="Coronary intervention">Coronary intervention</SimpleSelect.Option>
                    <SimpleSelect.Option value="LAA occlusion procedure">LAA occlusion procedure</SimpleSelect.Option>
                </SelectOther>

                <SelectOther
                    id="deviceType"
                    label="Device Type"
                    name="deviceType"
                    value={updatedPatient?.deviceType}
                    onChange={(e) => onPatientImplantDetailsChange({ ...updatedPatient, deviceType: e.target.value })}
                >
                    <SimpleSelect.Option value="">Select a device type</SimpleSelect.Option>
                    <SimpleSelect.Option value="Single Chamber Pacemaker">Single Chamber Pacemaker</SimpleSelect.Option>
                    <SimpleSelect.Option value="Dual Chamber Pacemaker">Dual Chamber Pacemaker</SimpleSelect.Option>
                    <SimpleSelect.Option value="Single Chamber Defibrillator">Single Chamber Defibrillator</SimpleSelect.Option>
                    <SimpleSelect.Option value="Dual Chamber Defibrillator">Dual Chamber Defibrillator</SimpleSelect.Option>
                    <SimpleSelect.Option value="Cardiac Resynchronisation Therapy Defibrillator">Cardiac Resynchronisation Therapy Defibrillator</SimpleSelect.Option>
                    <SimpleSelect.Option value="Cardiac Resynchronisation Therapy Pacemaker">Cardiac Resynchronisation Therapy Pacemaker</SimpleSelect.Option>
                    <SimpleSelect.Option value="Implantable Monitor">Implantable Monitor</SimpleSelect.Option>
                    <SimpleSelect.Option value="LAA occlusion device">LAA occlusion device</SimpleSelect.Option>
                    <SimpleSelect.Option value="Heart Valve">Heart Valve</SimpleSelect.Option>
                    <SimpleSelect.Option value="Leadless Pacemaker">Leadless Pacemaker</SimpleSelect.Option>
                    <SimpleSelect.Option value="Subcutaneous ICD">Subcutaneous ICD</SimpleSelect.Option>
                    <SimpleSelect.Option value="Extravascular ICD">Extravascular ICD</SimpleSelect.Option>
                    <SimpleSelect.Option value="Conduction System Pacing">Conduction System Pacing</SimpleSelect.Option>
                    <SimpleSelect.Option value="Temporary Pacing Systems">Temporary Pacing Systems</SimpleSelect.Option>
                    <SimpleSelect.Option value="Wearable Cardioverter Defibrillator">Wearable Cardioverter Defibrillator</SimpleSelect.Option>
                </SelectOther>

                <h5>Implanted Devices</h5>

                <ImplantInstanceList
                    patientId={updatedPatient.id}
                    patientNHSNumber={updatedPatient.nhsNumber}
                    patientFullName={updatedPatient.forename ? `${updatedPatient.forename} ${updatedPatient.surname}` : ""}
                    onDeviceListConditionChange={handleDeviceListConditionChange}
                />

                <Checkboxes>
                    <Checkboxes.Box
                        checked={updatedPatient?.mrConditional === true}
                        forceShowConditional={updatedPatient?.mrConditional == null}
                        conditional={updatedPatient?.mrConditional == null ? <p>Unknown</p> : undefined}
                        onChange={(e) => handleCheckboxChange('mrConditional', e.target.checked)}
                    >
                        <span style={{
                            color: updatedPatient?.mrConditional === true
                                ? 'green'
                                : updatedPatient?.mrConditional === false
                                    ? 'red'
                                    : undefined
                        }}>
                            System is MRI conditional?
                        </span>
                    </Checkboxes.Box>

                    <Checkboxes.Box
                        checked={updatedPatient?.abondonedHardware === true}
                        forceShowConditional={updatedPatient?.abondonedHardware == null}
                        conditional={updatedPatient?.abondonedHardware == null ? <p>Unknown</p> : undefined}
                        onChange={(e) => handleCheckboxChange('abondonedHardware', e.target.checked)}
                    >
                        <span style={{
                            color: updatedPatient?.abondonedHardware === true
                                ? 'red'
                                : updatedPatient?.abondonedHardware === false
                                    ? 'green'
                                    : undefined
                        }}>
                            Abandoned Lead Present
                        </span>
                    </Checkboxes.Box>

                    <Checkboxes.Box
                        conditional={<p>Active High Voltage Therapy.</p>}
                        checked={updatedPatient?.activeHighVoltageTherapy}
                        onChange={(e) => handleCheckboxChange('activeHighVoltageTherapy', e.target.checked)}
                    >
                        Active High Voltage Therapy?
                    </Checkboxes.Box>
                    <Checkboxes.Box
                        conditional={<p>Patient received appropriate HV therapy.</p>}
                        checked={updatedPatient?.patientReceivedAppropriateHVTherapy}
                        onChange={(e) => handleCheckboxChange('patientReceivedAppropriateHVTherapy', e.target.checked)}
                    >
                        Patient received appropriate HV therapy?
                    </Checkboxes.Box>
                    <Checkboxes.Box
                        conditional={<p>Patient received inappropriate HV therapy.</p>}
                        checked={updatedPatient?.patientReceivedInappropriateHVTherapy}
                        onChange={(e) => handleCheckboxChange('patientReceivedInappropriateHVTherapy', e.target.checked)}
                    >
                        Patient received inappropriate HV therapy?
                    </Checkboxes.Box>
                    <Checkboxes.Box
                        conditional={<p>Patient under active advisory.</p>}
                        checked={updatedPatient?.patientUnderActiveAdvisory}
                        onChange={(e) => handleCheckboxChange('patientUnderActiveAdvisory', e.target.checked)}
                    >
                        Patient under active advisory?
                    </Checkboxes.Box>
                </Checkboxes>

                <SelectOther
                    id="bradyprogrammedmode"
                    label="Brady Programmed Mode"
                    name="bradyProgrammedMode"
                    value={updatedPatient?.bradyProgrammedMode}
                    onChange={(e) => onPatientImplantDetailsChange({ ...updatedPatient, bradyProgrammedMode: e.target.value })}
                >
                    <SimpleSelect.Option value="">Select an option</SimpleSelect.Option>
                    <SimpleSelect.Option value="AAIR">AAIR</SimpleSelect.Option>
                    <SimpleSelect.Option value="VVI">VVI</SimpleSelect.Option>
                    <SimpleSelect.Option value="DDD">DDD</SimpleSelect.Option>
                    <SimpleSelect.Option value="AOO">AOO</SimpleSelect.Option>
                    <SimpleSelect.Option value="VDD">VDD</SimpleSelect.Option>
                    <SimpleSelect.Option value="DOO">DOO</SimpleSelect.Option>
                    <SimpleSelect.Option value="VVT">VVT</SimpleSelect.Option>
                </SelectOther>

                <div>
                    
                    {!['AAIR', 'VVI', 'DDD', 'AOO', 'VDD', 'DOO', 'VVT'].includes(updatedPatient?.bradyProgrammedMode) ? (
                        <Input
                            id="input-tachytherapyzonerate"
                            label="Tachy Therapy Zone Rate"
                            name="tachyTherapyZoneRate"
                            type="number"
                            value={updatedPatient?.tachyTherapyZoneRate}
                            onChange={(e) => onPatientImplantDetailsChange({ ...updatedPatient, tachyTherapyZoneRate: e.target.value })}
                        />
                    ) : (
                        <Input
                        id="input-bradyprogrammedlowerrate"
                        label="Brady Programmed Lower Rate"
                        name="bradyProgrammedLowerRate"
                        type="number"
                        value={updatedPatient?.bradyProgrammedLowerRate}
                        onChange={(e) => onPatientImplantDetailsChange({ ...updatedPatient, bradyProgrammedLowerRate: e.target.value })}
                    />
                    )
                
                }
                </div>
                <EntitySelect
                    title={"Implant Centres"}
                    singularEntity="implantCentre"
                    pluralEntity="implantCentres"
                    constantPrefix="IMPLANT_CENTRE"
                    listAction={listImplantCentres}
                    listByIdsAction={listImplantCentresByIds}
                    selectedIds={updatedPatient?.implantCentreIds || []}
                    onChange={(selectedImplantCentres) => {
                        const implantCentreIds = selectedImplantCentres.map(centre => centre.id);
                        onPatientImplantDetailsChange({
                            ...updatedPatient,
                            implantCentreIds,
                            implantCentreEntities: selectedImplantCentres
                        });
                    }}
                    summaryKeys={['name', 'address', 'city', 'country']}
                />

                <EntitySelect
                    title={"Followup Centres"}
                    singularEntity="followupCentre"
                    pluralEntity="followupCentres"
                    constantPrefix="FOLLOWUP_CENTRE"
                    listAction={listFollowupCentres}
                    listByIdsAction={listFollowupCentresByIds}
                    selectedIds={updatedPatient?.followUpCentreIds || []}
                    onChange={(selectedFollowupCentres) => {
                        const followUpCentreIds = selectedFollowupCentres.map(centre => centre.id);
                        onPatientImplantDetailsChange({
                            ...updatedPatient,
                            followUpCentreIds,
                            followUpCentreEntities: selectedFollowupCentres
                        });
                    }}
                    summaryKeys={['name', 'address', 'city', 'country']}
                />

                <EntitySelect
                    title={"Authorised Organisations"}
                    singularEntity="organisation"
                    pluralEntity="organisations"
                    constantPrefix="ORGANISATION"
                    listAction={listOrganisations}
                    listByIdsAction={listOrganisationsByIds}
                    selectedIds={updatedPatient?.organisationIds || []}
                    onChange={(selectedOrganisations) => {
                        const organisationIds = selectedOrganisations.map(org => org.id);
                        onPatientImplantDetailsChange({
                            ...updatedPatient,
                            organisationIds,
                            organisationEntities: selectedOrganisations
                        });
                    }}
                    summaryKeys={['name', 'address', 'city', 'country']}
                />

                <InsetText id="implant-history">
                    <p>Browse Practician Notes and Alerts</p>
                    <ActionLink
                        href="#implant-history"
                        onClick={() => {
                            if (updatedPatient.id) {
                                setOpenDrawer(true);
                            } else {
                                alert("Patient ID is required to view notes and alerts");
                            }
                        }}
                    >
                        Side notes / Alerts
                    </ActionLink>
                </InsetText>
            </Card.Content>

            <ImplantHistoryDrawer
                patientId={updatedPatient.id}
                patientNHSNumber={updatedPatient.nhsNumber}
                patientFullName={updatedPatient.forename ? `${updatedPatient.forename} ${updatedPatient.surname}` : ""}
                patientEmail={updatedPatient.email}
                isOpen={openDrawer}
                closeDrawer={() => setOpenDrawer(false)}
            />
        </Card>
    );
};

export default ImplantDetails;